/* ============================================================
   gd-ab-checkout.js — HELPER UNIVERSAL de cart permalink p/ A/B tests
   ------------------------------------------------------------
   PROBLEMA que este arquivo resolve (bug de Jul/2026):
     Um cart permalink `/cart/{v}:{q}?attributes[...]` RECONSTRÓI o carrinho do
     zero — só sobrevivem os attributes que a URL re-passar. Os pop-ups (ups-c,
     ups-b) só re-passavam `_lp_source`, então APAGAVAM as etiquetas dos testes
     (`_ab1_variant` etc.) ao navegar pro checkout → pedidos perdiam a atribuição
     e o dashboard subcontava a variante (ver abtests-documentation.md §🎰).

   SOLUÇÃO:
     Este helper monta o permalink lendo AUTOMATICAMENTE as etiquetas de TODOS os
     slots A/B ativos a partir do registry global `window.__gudayAbBuckets`. Todo
     pop-up (ups-c, ups-b, ab03-*, futuros) chama `gdAbCheckoutUrl(...)` — assim é
     IMPOSSÍVEL esquecer um slot. Fonte única, à prova de erro humano.

   COMO CADA MOTOR ALIMENTA O REGISTRY (contrato):
     window.__gudayAbBuckets['ab' + N] = { variant: '<bucket>', test: '<testName>' };
     (ex: window.__gudayAbBuckets.ab1 = { variant:'variant', test:'popup-crosssell-melatonina-v1' })
     Compat: se o valor for uma STRING (registry legado que guardava só o bucket),
     o helper trata como { variant: '<string>' } e omite o _ab{N}_test.

   USO no pop-up:
     window.location.href = gdAbCheckoutUrl({
       items: [{ id: comboVariantId, qty: 2 }, { id: melatoninaVariantId, qty: 1 }],
       lpSource: 'lp-5-razoes-bundles',        // opcional
       extraAttrs: { _ab3_popup: '1' },        // opcional: flags de funil deste teste
       discount: 'PRIMEIRAGUDAY'               // opcional: cupom auto-aplicado no checkout
     });

   CUPOM AUTO-APLICADO (opts.discount):
     Anexa `&discount=CODE` ao permalink. Como o cart permalink RECONSTRÓI o carrinho
     do zero (checkout novo) e o Shopify aceita 1 código por checkout, o cupom passado
     é o ÚNICO aplicado — não empilha nem herda cupom de sessão anterior. Cupom inválido
     é ignorado pelo Shopify (não quebra o checkout). Ver DOC-cupom-auto §lp-5-razoes-bundles.
   ============================================================ */
(function () {
  'use strict';
  if (window.gdAbCheckoutUrl) return; // idempotente (carregado 1x, usado por N pop-ups)

  function enc(s) { return encodeURIComponent(String(s == null ? '' : s)); }

  /* ── UTM: cache do carrinho (fix 28/Jul/2026) ────────────────────────────
     BUG: o permalink RECONSTRÓI o carrinho, então os `_utm_*` que o
     utm-tracker.js gravou eram APAGADOS ao ir pro checkout. O checkout chegava
     "sem origem" e a mídia perdia a atribuição (reportado pela consultoria de
     tracking em 27/Jul/2026). O `_lp_source`/`_ab*` já eram re-passados; a UTM
     nunca entrou nessa lista.

     POR QUE CACHE E NÃO FETCH NA HORA: os 7 call sites chamam este helper de
     forma SÍNCRONA (`return gdAbCheckoutUrl(...)` / `location.href = ...`).
     Tornar a função async exigiria reescrever os 7 pop-ups — mudança grande num
     caminho crítico. Em vez disso lemos o cart UMA vez no load e guardamos em
     memória; o helper continua síncrono e os call sites não mudam.

     FALLBACK: se o fetch ainda não voltou (clique muito rápido), lemos a UTM da
     PRÓPRIA URL. Com o gd-utm-passthrough.js no ar a UTM costuma estar na URL,
     então o fallback cobre bem a janela de corrida. */
  var UTM_KEYS = ['_utm_source','_utm_medium','_utm_campaign','_utm_content','_utm_term',
                  '_utm_channel','_utm_detail','_utm_referrer','_utm_ts'];
  var utmCache = null;

  try {
    var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json', 'X-LP-Tracker': '1' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var a = (cart && cart.attributes) || {};
        var out = {};
        UTM_KEYS.forEach(function (k) { if (a[k]) out[k] = a[k]; });
        utmCache = out;
      })
      .catch(function () { utmCache = {}; });
  } catch (e) { utmCache = {}; }

  // Fallback: deriva os _utm_* dos ?utm_* da URL atual.
  function utmFromUrl() {
    var out = {};
    try {
      var qs = new URLSearchParams(window.location.search);
      ['source','medium','campaign','content','term'].forEach(function (f) {
        var v = qs.get('utm_' + f);
        if (v) out['_utm_' + f] = v;
      });
    } catch (e) {}
    return out;
  }

  function utmAttrs() {
    if (utmCache && Object.keys(utmCache).length) return utmCache;
    return utmFromUrl();   // cart ainda não respondeu (ou veio vazio) → tenta a URL
  }

  // Lê o registry e devolve os attributes de TODOS os slots ativos:
  //   _ab{N}_test / _ab{N}_variant  (+ os genéricos _ab_test/_ab_variant do slot 1, p/ compat).
  function abAttrsFromRegistry() {
    var out = {};
    var reg = window.__gudayAbBuckets || {};
    Object.keys(reg).forEach(function (key) {
      var m = /^ab(\d+)$/.exec(key);      // 'ab1', 'ab2', 'ab3'...
      if (!m) return;
      var n = m[1];
      var raw = reg[key];
      // aceita { variant, test } (novo) ou string pura (legado = só o bucket)
      var variant = (raw && typeof raw === 'object') ? raw.variant : raw;
      var test = (raw && typeof raw === 'object') ? raw.test : null;
      if (!variant) return;
      out['_ab' + n + '_variant'] = variant;
      if (test) out['_ab' + n + '_test'] = test;
      // slot 1 também escreve os genéricos (dashboard/compat histórica)
      if (n === '1') {
        out['_ab_variant'] = variant;
        if (test) out['_ab_test'] = test;
      }
    });
    return out;
  }

  // Monta o permalink /cart/{id}:{qty},...?attributes[k]=v&...
  //   items:      [{id, qty}]  (obrigatório, ≥1)
  //   lpSource:   string       (opcional → attributes[_lp_source])
  //   extraAttrs: {k:v}        (opcional → flags de funil do próprio pop-up, ex _ab3_popup)
  //   attrs:      {k:v}        (opcional → override manual; tem prioridade sobre o registry)
  //   discount:   string       (opcional → &discount=CODE, cupom auto-aplicado no checkout)
  window.gdAbCheckoutUrl = function (opts) {
    opts = opts || {};
    var items = (opts.items || []).filter(function (it) { return it && it.id; });
    var path = '/cart/' + items.map(function (it) {
      return it.id + ':' + (it.qty || 1);
    }).join(',');

    // ordem de precedência: UTM (base) < registry < lpSource < extraAttrs < attrs (override)
    // A UTM entra PRIMEIRO (menor prioridade): re-passar a origem nunca pode
    // sobrescrever a etiqueta de um teste A/B nem o _lp_source.
    var all = {};
    var utm = utmAttrs();
    Object.keys(utm).forEach(function (k) { all[k] = utm[k]; });
    var abs = abAttrsFromRegistry();
    Object.keys(abs).forEach(function (k) { all[k] = abs[k]; });
    if (opts.lpSource) all['_lp_source'] = opts.lpSource;
    if (opts.extraAttrs) Object.keys(opts.extraAttrs).forEach(function (k) { all[k] = opts.extraAttrs[k]; });
    if (opts.attrs) Object.keys(opts.attrs).forEach(function (k) { all[k] = opts.attrs[k]; });

    var q = Object.keys(all)
      .filter(function (k) { return all[k] != null && all[k] !== ''; })
      .map(function (k) { return 'attributes[' + enc(k) + ']=' + enc(all[k]); });

    // cupom auto-aplicado: parâmetro nativo do cart permalink (não é um attribute).
    // Como o permalink reconstrói o carrinho, este é o único cupom que entra no checkout.
    if (opts.discount) q.push('discount=' + enc(opts.discount));

    return q.length ? (path + '?' + q.join('&')) : path;
  };
})();
